import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FilterX, Search } from "lucide-react";

interface TimeEntryFiltersProps {
  weekKey?: string;
  facilityId?: string;
  providerSearch?: string;
  onWeekChange: (week: string | undefined) => void;
  onFacilityChange: (facilityId: string | undefined) => void;
  onProviderSearchChange?: (search: string | undefined) => void;
  onClearFilters: () => void;
  facilities: { id: string; name: string }[];
  weeks: { key: string; label: string }[];
  showProviderSearch?: boolean;
}

export function TimeEntryFilters({
  weekKey,
  facilityId,
  providerSearch,
  onWeekChange,
  onFacilityChange,
  onProviderSearchChange,
  onClearFilters,
  facilities,
  weeks,
  showProviderSearch = false,
}: TimeEntryFiltersProps) {
  const [localProviderSearch, setLocalProviderSearch] = useState(
    providerSearch || "",
  );

  // Debounce provider search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onProviderSearchChange) {
        onProviderSearchChange(localProviderSearch || undefined);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localProviderSearch, onProviderSearchChange]);

  // Sync external state with local state
  useEffect(() => {
    setLocalProviderSearch(providerSearch || "");
  }, [providerSearch]);

  const hasFilters = weekKey || facilityId || providerSearch;

  const selectedFacilityName = facilities.find(
    (f) => f.id === facilityId,
  )?.name;
  const selectedWeekLabel = weeks.find((w) => w.key === weekKey)?.label;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3">
        <Select
          value={weekKey || "all"}
          onValueChange={(value) =>
            onWeekChange(value === "all" ? undefined : value)
          }
        >
          <SelectTrigger className="w-full sm:w-52">
            <SelectValue placeholder="All weeks" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Weeks</SelectItem>
            {weeks.map((week) => (
              <SelectItem key={week.key} value={week.key}>
                {week.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={facilityId || "all"}
          onValueChange={(value) =>
            onFacilityChange(value === "all" ? undefined : value)
          }
        >
          <SelectTrigger className="w-full sm:w-52">
            <SelectValue placeholder="All facilities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Facilities</SelectItem>
            {facilities.map((facility) => (
              <SelectItem key={facility.id} value={facility.id}>
                {facility.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showProviderSearch && (
          <div className="relative w-full sm:w-52">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search provider..."
              value={localProviderSearch}
              onChange={(e) => setLocalProviderSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        )}
      </div>

      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-slate-500">
            <FilterX className="h-3.5 w-3.5" />
            Active filters
          </span>
          {weekKey && selectedWeekLabel && (
            <Badge variant="outline" className="border-blue-200 text-blue-700">
              Week: {selectedWeekLabel}
            </Badge>
          )}
          {facilityId && selectedFacilityName && (
            <Badge variant="outline" className="border-blue-200 text-blue-700">
              Facility: {selectedFacilityName}
            </Badge>
          )}
          {providerSearch && (
            <Badge variant="outline" className="border-blue-200 text-blue-700">
              Provider: {providerSearch}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="h-7 px-3 text-slate-500 hover:text-slate-700"
          >
            <FilterX className="mr-1 h-3.5 w-3.5" />
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
